<script>
	import _ from 'underscore';
	import assignmentService from 'services/assignmentService';
	import { studentCompleteOfflineVocabActivity } from 'utilities/offlineUtilities';
	import pubsub from 'events/pubsub';

	const config = require('core/config');
	const { events } = require('utilities/analyticsUtilities');

	const WORDSMYTH_BASE = (config.resources.media.cdn || config.resources.media.origin) + '/ws/';
	const WORDSMYTH_SOUND_BASE = WORDSMYTH_BASE + 'sound/wedt/';

	const {
		fireEvent,
		events: { VOCAB_ACTIVITY_COMPLETE, VOCAB_ACTIVITY_RESET }
	} = require('utilities/analyticsUtilities');

	export default {
		components: {
			'audio-player-simple': require('components/audio-player-simple.vue').default,
			'text-to-speech-player-simple': require('components/text-to-speech-player-simple.vue').default,
			'vocab-powered-by-wordsmyth': require('components/vocab-powered-by-wordsmyth.vue').default
		},
		props: ['wordList', 'wordBuilder', 'assignmentId', 'productId', 'activityState', 'showTts'],
		modelData: ['offlineMode', 'printing'],
		data() {
			return {
				completedSyllables: {},
				selectedSyllable: null,
				draggingOver: null,
				dragging: null,
				shuffledSyllableList: [],
				events
			};
		},
		created() {
			this.reset(false);
			if (this.activityState && this.activityState.wb) {
				this.completeNow();
			}
		},
		methods: {
			reset(emitEvent = true) {
				const syllableList = [];
				this.wordList.forEach(word => {
					if (this.wordBuilder[word]) {
						this.wordBuilder[word].syllables.forEach((syllable, idx) => {
							syllableList.push({
								word,
								syllable,
								key: `${word}-${idx}`,
								x: Math.random() * 2 - 1,
								y: Math.random() * 2 - 1
							});
						});
					}
				});

				this.shuffledSyllableList = _.shuffle(syllableList);
				this.completedSyllables = {};
				this.selectedSyllable = null;

				if (emitEvent) {
					fireEvent(VOCAB_ACTIVITY_RESET, this.eventData);
				}
			},
			toggleSelectedSyllable(key) {
				if (this.selectedSyllable === key) {
					this.selectedSyllable = null;
				} else if (!this.completedSyllables[key]) {
					this.selectedSyllable = key;
				}
			},
			clickTargetWord(wordRec) {
				if (this.selectedSyllable && this.selectedSyllable.split(/-/)[0] === wordRec.word) {
					this.completedSyllables = _.assign({}, this.completedSyllables, {
						[this.selectedSyllable]: true
					});
					this.playWordIfComplete(wordRec.word);
				}
			},
			dragEnterWord(ev, wordRec) {
				if (this.dragging.split(/-/)[0] === wordRec.word) {
					this.draggingOver = wordRec.word;
					ev.preventDefault();
				}
			},
			startDragging(ev, key) {
				ev.dataTransfer.setData('text/plain', null);
				this.dragging = key;
				return true;
			},
			stopDragging() {
				this.dragging = null;
				this.draggingOver = null;
			},
			dropOnto(event, wordRec) {
				if (this.dragging.split(/-/)[0] === wordRec.word) {
					this.completedSyllables = _.assign({}, this.completedSyllables, {
						[this.dragging]: true
					});
					this.playWordIfComplete(wordRec.word);
				}
				this.dragging = null;
				this.draggingOver = null;
			},
			completeNow() {
				const completedSyllables = {};
				this.wordList.forEach(word => {
					if (this.wordBuilder[word]) {
						this.wordBuilder[word].syllables.forEach((syllable, idx) => {
							completedSyllables[word + '-' + idx] = true;
						});
					}
				});
				this.completedSyllables = completedSyllables;
				this.selectedSyllable = null;
			},
			playWordIfComplete(word) {
				let wordRec = null;
				let wordIdx = null;
				this.syllabizedWordList.forEach((sw, idx) => {
					if (sw.word === word) {
						wordIdx = idx;
						wordRec = sw;
					}
				});

				if (!wordRec) {
					return;
				}

				let anyMissing = false;
				wordRec.syllables.forEach((s, idx) => {
					if (!this.completedSyllables[word + '-' + idx]) {
						anyMissing = true;
					}
				});

				if (!anyMissing) {
					const players = _.filter(this.$children, c => c.$el.classList.contains('audio-player-simple'));
					players[wordIdx].play();
				}
			}
		},
		computed: {
			complete() {
				let totalSyllables = 0;
				this.syllabizedWordList.forEach(sw => {
					totalSyllables += sw.syllables.length;
				});

				return Object.keys(this.completedSyllables).length === totalSyllables;
			},
			syllabizedWordList() {
				const wordList = [];
				this.wordList.forEach(word => {
					if (this.wordBuilder[word]) {
						wordList.push(
							_.assign({}, this.wordBuilder[word], {
								word,
								audioUrl: WORDSMYTH_SOUND_BASE + this.wordBuilder[word].pronunciation,
								eventData: {
									pid: this.productId || undefined,
									aid: this.assignmentId || undefined,
									word
								}
							})
						);
					}
				});
				return wordList;
			},
			eventData() {
				return {
					aid: this.assignmentId,
					type: 'wb'
				};
			}
		},
		watch: {
			completedSyllables() {
				this.dragging = null;
				this.draggingOver = null;
				this.selectedSyllable = null;
			},
			complete(c) {
				const data = this.eventData;
				if (c && this.assignmentId) {
					fireEvent(VOCAB_ACTIVITY_COMPLETE, data);
				}
				if (c && this.activityState && this.assignmentId && !this.activityState.wb) {
					let prms = null;
					if (this.offlineMode) {
						prms = studentCompleteOfflineVocabActivity(data);
					} else {
						prms = assignmentService.completeVocabActivity(data);
					}

					prms.then(() => {
						pubsub.publish('vocab-activity-complete', { wb: true });
					});
				}
			}
		}
	};
</script>
<template>
	<div
		class="vocab-builder-activity-container"
		:class="{ printing: printing }">
		<header>
			<h3><span v-if="printing">2.</span> Word Builder</h3>
			<p v-if="printing">
				Words are made of smaller parts that are called syllables. Some words have one syllable, but many have two or more. Draw lines to show how the
				syllables build each vocabulary word.
			</p>
			<p v-else>
				Words are made of smaller parts that are called syllables. Some words have one syllable, but many have two or more. Listen to the audio reading
				aloud each vocabulary word. Then move the syllables to build each word.
				<text-to-speech-player-simple
					v-if="showTts"
					text="Words are made of smaller parts that are called syllables. Some words have one syllable, but many have two or more. Listen to the audio reading aloud each vocabulary word. Then move the syllables to build each word."
					:event-data="{
						aid: assignmentId,
						pid: product && product.id,
						tag: 'vocab_builder_instructions'
					}">
				</text-to-speech-player-simple>
			</p>
		</header>
		<div
			class="vocab-activity vocab-builder-activity"
			:class="{ completed: complete }">
			<h2 class="completion-header">Congratulations!</h2>
			<button
				v-if="complete"
				class="button -outline button-reset"
				@click="reset">
				Reset
			</button>
			<div
				class="syllable-bank"
				v-else>
				<div
					v-for="syllableRec in shuffledSyllableList"
					:key="syllableRec.key"
					class="syllable-wrapper"
					@click="toggleSelectedSyllable(syllableRec.key)"
					:class="{
						completed: completedSyllables[syllableRec.key],
						selected: selectedSyllable === syllableRec.key
					}">
					<div class="syllable">
						<div
							draggable="true"
							:id="'drag-' + syllableRec.key"
							:class="{ completed: !!completedSyllables[syllableRec.key] }"
							@dragstart="startDragging($event, syllableRec.key)"
							@dragend="stopDragging"
							:style="{ top: `${syllableRec.y}rem`, left: `${syllableRec.x}rem` }">
							{{ syllableRec.syllable }}
						</div>
					</div>
				</div>
			</div>
			<div class="words">
				<div
					v-for="(wordRec, index) in syllabizedWordList"
					class="word"
					:key="wordRec.word">
					<span v-if="printing">{{ index + 1 }}.</span>
					<div
						:class="{
							'dragging-over': draggingOver === wordRec.word,
							'ready-for-syllable': !!selectedSyllable
						}"
						@click="clickTargetWord(wordRec)"
						@dragover="dragEnterWord($event, wordRec)"
						@drop="dropOnto($event, wordRec)"
						@dragleave="draggingOver = null">
						<audio-player-simple
							no-pause
							@click.native.stop
							:url="wordRec.audioUrl"
							:event-data="wordRec.eventData"
							:audio-play-event-name="events.VOCAB_BUILDER_AUDIO_PLAY"
							:audio-pause-event-name="events.VOCAB_BUILDER_AUDIO_PAUSE"
							:audio-complete-event-name="events.VOCAB_BUILDER_AUDIO_COMPLETE">
						</audio-player-simple>
						<span
							v-for="(syl, sylIdx) in wordRec.syllables"
							:key="sylIdx"
							class="syllable"
							:class="{
								completed: !!completedSyllables[wordRec.word + '-' + sylIdx]
							}">
							{{ syl }}
						</span>
					</div>
				</div>
			</div>
			<vocab-powered-by-wordsmyth></vocab-powered-by-wordsmyth>
		</div>
	</div>
</template>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';

	.vocab-builder-activity {
		h2 {
			text-align: center;
		}

		.syllable-bank {
			text-align: center;

			.syllable-wrapper {
				display: inline-block;
				width: 33.33%;

				@media only screen and (min-width: $bp-width-med) {
					width: 6rem;
				}

				height: 4rem;
				position: relative;

				.syllable {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translateX(-50%) translateY(-50%);
					font-weight: bold;
					font-size: 19px;
					color: $c-blue;

					> div {
						position: relative;
						padding: 0 0.1rem;
						-webkit-user-drag: element;
					}
				}

				&.selected .syllable > div {
					color: white;
					background-color: $c-blue;
				}

				&.completed .syllable {
					display: none;
				}
			}
		}

		.words {
			text-align: center;
		}

		.word {
			display: inline-block;

			> div {
				border: 2px solid $c-blue;
				margin: 1rem;
				transition: margin 0.3s;
				padding: 0.3rem 0.6rem 0.3rem 1.65rem;
				border-radius: 4px;
				color: $c-blue;
				font-weight: bold;
				display: inline-block;
				line-height: 1.5;
				position: relative;
				font-size: 19px;

				.audio-player-simple {
					position: absolute;
					top: 0.44rem;
					left: 0.28rem;
				}

				&.dragging-over {
					background-color: $c-blue;
				}

				&.ready-for-syllable {
					transform-origin: center;
					animation-duration: 1.5s;
					animation-iteration-count: infinite;
					animation-name: pulsing_builder_button;
				}

				.syllable {
					visibility: hidden;

					&.completed {
						// animation-name: changer_word_completed;
						animation-duration: 0.7s;
						visibility: visible;
					}
				}
			}
		}

		&.completed {
			.word > div {
				border-color: transparent;
				margin: 1rem 2.3rem;
			}
		}

		@keyframes pulsing_builder_button {
			0% {
				transform: scale(1);
			}

			50% {
				transform: scale(1.07);
			}

			100% {
				transform: scale(1);
			}
		}
	}

	.vocab-builder-activity-container {
		&.printing {
			.vocab-builder-activity {
				border: 1px solid;
				margin-top: 1rem;

				.syllable {
					font-size: 0.875rem;
					font-weight: 400;
				}
			}

			.syllable-bank {
				padding-top: 1rem;
			}

			.syllable-wrapper {
				height: 2rem;
			}

			.words {
				padding: 1rem;
				text-align: left;
			}

			.word {
				> span:not(:first-child) {
					margin-left: 3rem;
				}

				> div {
					border: none;
					border-bottom: 1px solid;
					border-radius: 0;
					width: 10rem;
					margin: 0;
				}
			}
		}
	}
</style>
